import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBookOpen,
  FiCheck,
  FiCode,
  FiDatabase,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiFilter,
  FiGithub,
  FiPlay,
  FiPlus,
  FiRss,
  FiStar,
  FiX,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { getApiUrl } from '../utils/api';

// ---------- Types ----------

interface SourcePublication {
  slug: string;
  display_name?: string | null;
}

interface NotebookRecipe {
  dataset_pattern_id: string;
  workflow_pattern_ids: string[];
  variable_bindings: Record<string, unknown>;
  narrative_md: string;
}

type Classification =
  | 'notebook_high_fidelity'
  | 'notebook_sketch'
  | 'findings_only';

interface Actionability {
  classification: Classification;
  datasets_referenced: string[];
  sdk_functions_used: string[];
  recommended_gpu_tier: string | null;
  notebook_recipe: NotebookRecipe | null;
  next_steps: string[];
  fidelity_score: number | null;
  known_gaps: string[];
  findings_summary: string | null;
  key_findings: string[];
  findings_only_reason:
    | 'dataset_gap'
    | 'infra_gap'
    | 'survey'
    | 'theory'
    | 'proprietary_data'
    | 'other'
    | null;
  missing_datasets: string[];
}

interface CodeReference {
  url: string;
  host: string;
  stars: number | null;
  language: string | null;
  framework: string | null;
  is_official: boolean;
  discovered_via: string;
}

interface StarterNotebookInfo {
  count: number;
  latest_s3_key: string;
  latest_modified_at: string | null;
}

interface ResearchInsight {
  public_id: string;
  week_iso: string;
  paper_title: string;
  paper_url: string;
  source_type: string | null;
  source_publications: SourcePublication[];
  corroborated: boolean;
  propalgos_relevance: 'high' | 'medium' | 'low' | 'none';
  feature_tags: string[] | null;
  summary_md: string | null;
  related_template_slug: string | null;
  dismissed: boolean;
  actionability: Actionability | null;
  recommended_gpu_tier: string | null;
  starter_notebook: StarterNotebookInfo | null;
  classification: Classification | null;
  fidelity_score: number | null;
  code_references: CodeReference[];
  paper_published_at: string | null;
  // 1hr-TTL presigned URL to the gpt-image-2 infographic when one exists
  // (Top Picks only — generated via the admin Regenerate button). Null
  // when no infographic has been generated for this insight.
  infographic_url?: string | null;
}

interface PaginatedResponse {
  items: ResearchInsight[];
  next_offset: number | null;
  total: number | null;
}

interface NotebookVersion {
  key: string;
  size: number;
  last_modified: string | null;
  kind: 'base' | 'timestamped';
}

interface NotebookVersionsResponse {
  insight_public_id: string;
  base_key: string;
  versions: NotebookVersion[];
}

interface Category {
  slug: string;
  label: string;
  tags: string[];
}

interface WeekEntry {
  iso: string;           // "2026-W16"
  start_date: string;    // "2026-04-13"
  end_date: string;      // "2026-04-19"
  has_summary: boolean;  // true when a published newsletter exists
}

interface FeedMeta {
  categories: Category[];
  weeks: WeekEntry[];
}

interface ToastAction {
  label: string;
  onClick: () => void;
  primary?: boolean;
}

interface ToastState {
  message: string;
  actions?: ToastAction[];
}

// ---------- Weekly summary types (matches backend WeeklySummaryResponse) ----------

interface SummaryPaperRef {
  insight_public_id: string;
  blurb: string;
}

interface SummaryDatasetEntry {
  token: string;
  paper_count: number;
  insight_public_ids?: string[];
  blurb: string;
}

interface ThemeEntry {
  label: string;
  insight_public_ids: string[];
}

interface TopPickEntry {
  insight_public_id: string;
  headline: string;
  narrative_md: string;
  infographic_brief: string;
  // Optional — populated by the backend when an infographic has been
  // generated and uploaded to the shared bucket. Null until then.
  infographic_url?: string | null;
}

interface WeeklySummaryContent {
  intro: string;
  // Legacy rows have `string[]`; new rows have `ThemeEntry[]` with
  // click-to-filter wiring. Renderers must handle both.
  themes: Array<string | ThemeEntry>;
  excited_to_try: SummaryPaperRef[];
  still_early: SummaryPaperRef[];
  new_datasets: SummaryDatasetEntry[];
  closing_nudge: string;
  // v3 — deterministic Top-3 lead. Optional on the type so older summary
  // rows (pre-v3) still render through the same component.
  top_picks?: TopPickEntry[];
}

interface WeeklySummaryResponse {
  week_iso: string;
  status: string;
  content: WeeklySummaryContent;
  model_used?: string | null;
  input_paper_count?: number | null;
  generated_at?: string | null;
  published_at?: string | null;
}

// ---------- Constants ----------

const RELEVANCE_BADGE: Record<ResearchInsight['propalgos_relevance'], string> = {
  high: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  medium: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  low: 'border-neutral-700 bg-neutral-800 text-neutral-400',
  none: 'border-neutral-800 bg-neutral-900 text-neutral-500',
};

function relevanceTooltip(rel: ResearchInsight['propalgos_relevance']): string {
  const base = 'PropAlgos relevance — how directly this paper maps onto our trading/backtest surface, scored by keyword match against PropAlgos-friendly themes (factor investing, regime detection, options, volatility, ML-on-returns, etc.). ';
  switch (rel) {
    case 'high': return base + 'HIGH = you can put this to work on a PropAlgos strategy this week. Prioritize these for competition submissions.';
    case 'medium': return base + 'MEDIUM = relevant domain knowledge — takes some translation to become a trading edge, but worth the read.';
    case 'low': return base + 'LOW = tangentially related to quant but not obviously actionable on the platform today.';
    case 'none': return base + 'NONE = keyword-scored as off-topic. Usually shown because a corroborating newsletter still flagged it.';
  }
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'notebook_first', label: 'Notebooks first' },
  { value: 'corroborated', label: 'Corroborated first' },
  { value: 'newest', label: 'Newest' },
  { value: 'fidelity', label: 'Highest fidelity' },
];

type ClassificationFilter = 'all' | 'notebook' | 'findings';
const CLASSIFICATION_OPTIONS: { value: ClassificationFilter; label: string }[] = [
  { value: 'all', label: 'All cards' },
  { value: 'notebook', label: 'Notebooks only' },
  { value: 'findings', label: 'Findings only' },
];

type TimeframeFilter = 'all' | '7' | '30' | '90';
const TIMEFRAME_OPTIONS: { value: TimeframeFilter; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: '90', label: 'Last 90 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
];

const PAGE_SIZE = 20;
const SUMMARY_MIN_HEIGHT_CLASS = 'min-h-[4.5rem]';
const TAGLINE_THRESHOLD = 100;

const REASON_LABEL: Record<string, string> = {
  dataset_gap: 'Needs data PropAlgos doesn\'t have yet',
  infra_gap: 'Needs infrastructure PropAlgos doesn\'t support',
  survey: 'Survey / review paper',
  theory: 'Theoretical — no empirical reproduction',
  proprietary_data: 'Uses proprietary data',
  other: 'Worth reading, not runnable as-is',
};

// Display name for a recommended GPU tier id. Cards show it as a subtle
// "Runs on {label}" hint; the post-generate toast uses the same label for
// the "Launch on {label}" action. Keep this small and local until we have
// a second consumer worth extracting to a util module.
const GPU_TIER_LABEL: Record<string, string> = {
  cpu: 'CPU',
  t4: 'T4 GPU',
  a10g: 'A10G GPU',
  a100: 'A100 GPU',
  h100: 'H100 GPU',
  l4: 'L4 GPU',
  l40s: 'L40S GPU',
};
function tierDisplay(tier: string | null | undefined): string | null {
  if (!tier) return null;
  return GPU_TIER_LABEL[tier.toLowerCase()] ?? tier.toUpperCase();
}

// ISO week "YYYY-Www" → human date range like "Apr 13 – 19, 2026".
// Uses the ISO-8601 rule that week 1 contains Jan 4, so week N's Monday
// is (Jan 4 of that ISO year) shifted back to the nearest prior Monday,
// then forward by (N-1) weeks. Falls back to the raw string for malformed
// input so the UI never crashes on a bad week_iso.
function formatWeekRange(weekIso: string | null | undefined): string {
  if (!weekIso) return '';
  const m = /^(\d{4})-W(\d{2})$/.exec(weekIso);
  if (!m) return weekIso;
  const isoYear = Number(m[1]);
  const isoWeek = Number(m[2]);
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  // getUTCDay: Sun=0..Sat=6. Shift back to Monday (ISO weekday 1).
  const jan4Dow = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4.getTime());
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const monday = new Date(week1Monday.getTime());
  monday.setUTCDate(week1Monday.getUTCDate() + (isoWeek - 1) * 7);
  const sunday = new Date(monday.getTime());
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const monthShort = (d: Date) =>
    d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const mMonth = monthShort(monday);
  const sMonth = monthShort(sunday);
  const mDay = monday.getUTCDate();
  const sDay = sunday.getUTCDate();
  const year = sunday.getUTCFullYear();
  if (mMonth === sMonth) {
    return `${mMonth} ${mDay} – ${sDay}, ${year}`;
  }
  return `${mMonth} ${mDay} – ${sMonth} ${sDay}, ${year}`;
}

// ---------- Component ----------

interface OverwriteDialogState {
  insight: ResearchInsight;
  existing: NotebookVersionsResponse;
}

// Build the post-generate toast. When the insight's recipe surfaced a
// recommended GPU tier, show both actions; otherwise fall back to the
// single "Open notebook" button. Navigation, not auto-launch, satisfies
// the "not pushy" product constraint — the user still confirms the
// instance launch on the compute page.
function buildGenerateToast(
  body: { s3_key?: string; recommended_gpu_tier?: string | null },
  navigate: (to: string) => void,
  titlePrefix: string = 'Starter notebook ready.',
): ToastState {
  const s3Key = body.s3_key ?? '';
  const tier = body.recommended_gpu_tier ?? null;
  const actions: ToastAction[] = [
    {
      label: 'Open notebook',
      onClick: () => navigate('/dashboard/notebooks'),
      primary: true,
    },
  ];
  const tierLabel = tierDisplay(tier);
  // CPU tier → no launcher path exists for it, so skip the secondary action
  // rather than point users at a compute page that won't show a matching
  // instance. GPU tiers get the launch deep link.
  const isLaunchable = Boolean(tier) && tier !== 'cpu' && Boolean(tierLabel);
  if (tier && tierLabel && isLaunchable) {
    const qs = new URLSearchParams({ tier, notebook: s3Key });
    actions.push({
      label: `Launch on ${tierLabel} →`,
      onClick: () => navigate(`/dashboard/compute?${qs.toString()}`),
    });
  }
  return { message: titlePrefix, actions };
}

export default function ResearchFeedPage() {
  const navigate = useNavigate();

  // Meta + data state
  const [meta, setMeta] = useState<FeedMeta | null>(null);
  const [insights, setInsights] = useState<ResearchInsight[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editorial weekly summary (Opus-generated, admin-published). Null when
  // no summary is published yet — panel silently omits in that case.
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummaryResponse | null>(null);
  // Click a theme chip in WeeklySummaryPanel → filter the feed below to
  // the papers the editor tagged with that theme. Null = no theme filter.
  const [themeFilter, setThemeFilter] = useState<{ label: string; ids: Set<string> } | null>(null);
  // Set of insight public_ids the user has already requested datasets for
  // — populated once via `/my-dataset-requests` so cards can render the
  // "Already requested ✓" state without per-card API calls.
  const [requestedInsightIds, setRequestedInsightIds] = useState<Set<string>>(new Set());

  // Per-card state
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [requestingDatasetId, setRequestingDatasetId] = useState<string | null>(null);
  const [overwriteDialog, setOverwriteDialog] = useState<OverwriteDialogState | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Filters
  const [sort, setSort] = useState<string>('notebook_first');
  const [classification, setClassification] = useState<ClassificationFilter>('all');
  const [timeframe, setTimeframe] = useState<TimeframeFilter>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [hasCode, setHasCode] = useState<boolean>(false);
  const [hasNotebook, setHasNotebook] = useState<boolean>(false);
  // Week navigation state. `weekMode === 'all'` hides the newsletter and
  // drops the week filter; `'single'` pins to one week and renders its
  // newsletter (if published). `week` is the ISO week string in single
  // mode, empty in all mode.
  const [weekMode, setWeekMode] = useState<'single' | 'all'>('single');
  const [week, setWeek] = useState<string>('');
  // Raw search input + the debounced value that actually feeds the query
  // string. 250ms feels snappy without hammering the endpoint per keystroke.
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');

  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);

  // Switching weeks invalidates the theme filter (ids belong to the
  // prior week's paper set).
  useEffect(() => { setThemeFilter(null); }, [week, weekMode]);

  const loadSentinel = useRef<HTMLDivElement | null>(null);

  // Bootstrap meta once, then seed the default week: pin to the most
  // recent week that has a published newsletter so first paint shows the
  // editorial. If nothing has a summary, fall back to "All weeks" mode so
  // we don't render an empty newsletter placeholder.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch(`${getApiUrl()}/api/v1/research-insights/meta`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d: FeedMeta) => {
        setMeta(d);
        const firstWithSummary = (d.weeks || []).find((w) => w.has_summary);
        if (firstWithSummary) {
          setWeek(firstWithSummary.iso);
          setWeekMode('single');
        } else {
          setWeek('');
          setWeekMode('all');
        }
      })
      .catch((e) => console.warn('Failed to load feed meta', e));
  }, []);

  // Fetch the weekly summary for the currently selected week. In "all"
  // mode we explicitly clear the summary so the newsletter panel hides.
  useEffect(() => {
    if (weekMode === 'all' || !week) {
      setWeeklySummary(null);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch(
      `${getApiUrl()}/api/v1/research-insights/weekly-summary?week=${encodeURIComponent(week)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setWeeklySummary(d && d.content ? (d as WeeklySummaryResponse) : null))
      .catch((e) => {
        console.warn('Failed to load weekly summary', e);
        setWeeklySummary(null);
      });
  }, [weekMode, week]);

  // One-shot: what datasets has this user already requested? Feeds the
  // per-card "Dataset requested ✓" state.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch(`${getApiUrl()}/api/v1/research-insights/my-dataset-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { insight_public_ids: [] }))
      .then((d) => setRequestedInsightIds(new Set(d.insight_public_ids || [])))
      .catch((e) => console.warn('Failed to load dataset requests', e));
  }, []);

  // Debounce the keyword search input so each keystroke doesn't fire a
  // list query. 250ms is the sweet spot — fast enough to feel live but
  // slow enough to collapse a typical word into one request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Compose query string from current filters
  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sort', sort);
    params.set('classification', classification);
    params.set('limit', String(PAGE_SIZE));
    if (category) params.set('category', category);
    if (hasCode) params.set('has_code', 'true');
    if (hasNotebook) params.set('has_notebook', 'true');
    // Week filter only applies in single-week mode; 'all' mode shows the
    // full cross-week feed (and hides the newsletter).
    if (weekMode === 'single' && week) params.set('week', week);
    if (debouncedQuery) params.set('q', debouncedQuery);
    if (timeframe !== 'all') {
      const days = Number(timeframe);
      const since = new Date();
      since.setDate(since.getDate() - days);
      params.set('published_since', since.toISOString().slice(0, 10));
    }
    params.set('min_relevance', 'medium');
    return params;
  }, [sort, classification, category, hasCode, hasNotebook, weekMode, week, debouncedQuery, timeframe]);

  // Fetch a page (initial or append)
  const fetchPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams(filterQuery);
        params.set('offset', String(pageOffset));
        const resp = await fetch(
          `${getApiUrl()}/api/v1/research-insights?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);
        const data = (await resp.json()) as PaginatedResponse;
        setInsights((prev) => (append ? [...prev, ...data.items] : data.items));
        setTotal(data.total);
        setNextOffset(data.next_offset);
        setOffset(pageOffset);
      } catch (e) {
        console.error('Failed to load research insights:', e);
        setError('Could not load research insights.');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filterQuery, navigate],
  );

  // Filter change → reset + fetch page 0
  useEffect(() => {
    setInsights([]);
    setNextOffset(null);
    fetchPage(0, false);
  }, [fetchPage]);

  // Infinite scroll: observe the sentinel; when intersecting and there's more
  // and we're not already loading, fetch next page.
  useEffect(() => {
    const el = loadSentinel.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !loading &&
          !loadingMore &&
          nextOffset != null
        ) {
          fetchPage(nextOffset, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextOffset, loading, loadingMore, fetchPage]);

  // ---------- Handlers ----------

  const handleDismiss = async (id: string) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setDismissing(id);
    try {
      await fetch(`${getApiUrl()}/api/v1/research-insights/${id}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsights((prev) => prev.filter((i) => i.public_id !== id));
    } catch (e) {
      console.error('Dismiss failed:', e);
    } finally {
      setDismissing(null);
    }
  };

  const handleOpenTemplate = (slug: string) => {
    sessionStorage.setItem('strategy_library_filter_slug', slug);
    navigate('/dashboard/strategy-library');
  };

  const callGenerate = useCallback(
    async (insightId: string, mode: 'create' | 'overwrite' | 'timestamped') => {
      const token = localStorage.getItem('authToken');
      if (!token) return undefined;
      return fetch(
        `${getApiUrl()}/api/v1/research-insights/${insightId}/starter-notebook`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mode }),
        },
      );
    },
    [],
  );

  const handleGenerateStarter = useCallback(
    async (insight: ResearchInsight) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }
      setGeneratingId(insight.public_id);
      try {
        const versionsResp = await fetch(
          `${getApiUrl()}/api/v1/research-insights/${insight.public_id}/starter-notebook/versions`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!versionsResp.ok) throw new Error(`Versions lookup failed (${versionsResp.status})`);
        const versions = (await versionsResp.json()) as NotebookVersionsResponse;
        if (versions.versions.length > 0) {
          setOverwriteDialog({ insight, existing: versions });
          return;
        }
        const resp = await callGenerate(insight.public_id, 'create');
        if (!resp) return;
        if (resp.status === 409) {
          const versionsAgain = await fetch(
            `${getApiUrl()}/api/v1/research-insights/${insight.public_id}/starter-notebook/versions`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (versionsAgain.ok) {
            setOverwriteDialog({
              insight,
              existing: (await versionsAgain.json()) as NotebookVersionsResponse,
            });
          }
          return;
        }
        if (!resp.ok) throw new Error(`Generate failed (${resp.status})`);
        const body = await resp.json();
        setToast(buildGenerateToast(body, navigate));
      } catch (e) {
        console.error('Starter notebook generation failed:', e);
        setToast({ message: `Could not generate notebook: ${(e as Error).message}` });
      } finally {
        setGeneratingId(null);
      }
    },
    [callGenerate, navigate],
  );

  const handleOpenNotebook = useCallback(
    async (_insight: ResearchInsight) => {
      navigate('/dashboard/notebooks');
    },
    [navigate],
  );

  const handleGenerateNew = useCallback(
    async (insight: ResearchInsight) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const versionsResp = await fetch(
          `${getApiUrl()}/api/v1/research-insights/${insight.public_id}/starter-notebook/versions`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!versionsResp.ok) throw new Error(`Versions lookup failed (${versionsResp.status})`);
        const versions = (await versionsResp.json()) as NotebookVersionsResponse;
        setOverwriteDialog({ insight, existing: versions });
      } catch (e) {
        console.error('Versions lookup failed:', e);
        setToast({ message: `Could not open dialog: ${(e as Error).message}` });
      }
    },
    [navigate],
  );

  const handleRequestDataset = useCallback(
    async (insight: ResearchInsight) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }
      if (requestedInsightIds.has(insight.public_id)) return;
      const missing = insight.actionability?.missing_datasets ?? [];
      setRequestingDatasetId(insight.public_id);
      try {
        const resp = await fetch(
          `${getApiUrl()}/api/v1/research-insights/${insight.public_id}/request-dataset`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ note: null }),
          },
        );
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Request failed (${resp.status}): ${txt.slice(0, 120)}`);
        }
        setRequestedInsightIds((prev) => {
          const next = new Set(prev);
          next.add(insight.public_id);
          return next;
        });
        const preview = missing.slice(0, 2).join(', ') + (missing.length > 2 ? '…' : '');
        setToast({
          message: `Got it — we'll prioritize ${preview || 'that dataset'}. Admin will reach out when it lands.`,
        });
      } catch (e) {
        console.error('Dataset request failed:', e);
        setToast({ message: `Could not log request: ${(e as Error).message}` });
      } finally {
        setRequestingDatasetId(null);
      }
    },
    [navigate, requestedInsightIds],
  );

  const handleOverwriteDecision = useCallback(
    async (mode: 'overwrite' | 'timestamped') => {
      if (!overwriteDialog) return;
      const { insight } = overwriteDialog;
      setGeneratingId(insight.public_id);
      setOverwriteDialog(null);
      try {
        const resp = await callGenerate(insight.public_id, mode);
        if (!resp || !resp.ok) throw new Error(`Generate failed (${resp?.status || 'unknown'})`);
        const body = await resp.json();
        const titlePrefix = mode === 'timestamped' ? 'Saved as new timestamped copy.' : 'Overwrote starter notebook.';
        setToast(buildGenerateToast(body, navigate, titlePrefix));
      } catch (e) {
        console.error('Starter notebook generation failed:', e);
        setToast({ message: `Could not generate notebook: ${(e as Error).message}` });
      } finally {
        setGeneratingId(null);
      }
    },
    [callGenerate, navigate, overwriteDialog],
  );

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---------- Render ----------

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-neutral-500">
            <FiRss className="h-4 w-4" />
            <span>Research Feed</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold">This week in quant research</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-400">
            Papers ranked for PropAlgos relevance, synthesized from quant newsletters
            each week. Star = multiple independent sources flagged it. Cards marked{' '}
            <span className="text-amber-300">Sketch</span> or{' '}
            <span className="text-emerald-300">Reproducible</span> can be spun up as
            runnable starter notebooks — hit <span className="text-emerald-300">Generate starter notebook</span> and
            you're in a working backtest in under a minute.
          </p>
        </header>

        {weeklySummary && weeklySummary.content.top_picks && weeklySummary.content.top_picks.length > 0 && (
          <TopPicksHero
            picks={weeklySummary.content.top_picks}
            onPickClick={scrollToInsightCard}
          />
        )}

        {weeklySummary && (
          <WeeklySummaryPanel
            summary={weeklySummary}
            insights={insights}
            onThemeClick={(theme) => {
              setThemeFilter({ label: theme.label, ids: new Set(theme.insight_public_ids) });
              // Scroll the feed into view.
              requestAnimationFrame(() => {
                const target = document.getElementById('feed-cards-top');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              });
            }}
            activeThemeLabel={themeFilter?.label ?? null}
          />
        )}

        <FilterBar
          meta={meta}
          sort={sort}
          onSortChange={setSort}
          classification={classification}
          onClassificationChange={setClassification}
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          category={category}
          onCategoryChange={setCategory}
          hasCode={hasCode}
          onHasCodeChange={setHasCode}
          hasNotebook={hasNotebook}
          onHasNotebookChange={setHasNotebook}
          weekMode={weekMode}
          week={week}
          onWeekModeChange={setWeekMode}
          onWeekChange={setWeek}
          query={query}
          onQueryChange={setQuery}
          totalCount={total}
          loadedCount={insights.length}
        />

        {error && (
          <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div id="feed-cards-top" />

        {themeFilter && (
          <div className="mb-4 flex items-center gap-3 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-sm">
            <span className="text-indigo-200">Theme filter:</span>
            <span className="font-medium text-indigo-100">{themeFilter.label}</span>
            <span className="text-xs text-indigo-300/70">
              · {Array.from(themeFilter.ids).filter((id) => insights.some((i) => i.public_id === id)).length} matching paper(s)
            </span>
            <button
              type="button"
              onClick={() => setThemeFilter(null)}
              className="ml-auto text-xs text-indigo-300 hover:text-white"
            >
              clear ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white" />
          </div>
        ) : insights.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            {(themeFilter
              ? insights.filter((i) => themeFilter.ids.has(i.public_id))
              : insights
            ).map((insight) => (
              <InsightCard
                key={insight.public_id}
                insight={insight}
                onDismiss={handleDismiss}
                onOpenTemplate={handleOpenTemplate}
                onGenerateStarter={handleGenerateStarter}
                onOpenNotebook={handleOpenNotebook}
                onGenerateNew={handleGenerateNew}
                onRequestDataset={handleRequestDataset}
                dismissing={dismissing === insight.public_id}
                generating={generatingId === insight.public_id}
                alreadyRequested={requestedInsightIds.has(insight.public_id)}
                requestingDataset={requestingDatasetId === insight.public_id}
              />
            ))}
            <div ref={loadSentinel} className="py-6 text-center text-xs text-neutral-600">
              {loadingMore
                ? 'Loading more…'
                : nextOffset == null
                  ? '— end of feed —'
                  : ''}
            </div>
          </div>
        )}
      </div>

      {overwriteDialog && (
        <OverwriteDialog
          state={overwriteDialog}
          onCancel={() => setOverwriteDialog(null)}
          onConfirm={handleOverwriteDecision}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 max-w-sm rounded-md border border-neutral-700 bg-neutral-900 px-4 py-3 text-sm text-neutral-200 shadow-lg">
          <div>{toast.message}</div>
          {toast.actions && toast.actions.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {toast.actions.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => {
                    a.onClick();
                    setToast(null);
                  }}
                  className={
                    a.primary
                      ? 'rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black transition hover:bg-neutral-200'
                      : 'rounded-md border border-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-200 transition hover:border-neutral-500'
                  }
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Weekly editorial summary panel ----------

/** Scroll to (and briefly highlight) the card for a given insight. Used by
 * both the inline paper links inside summary blurbs and the dataset-section
 * "see papers" affordances. */
function scrollToInsightCard(publicId: string): void {
  const el = document.querySelector(`[data-insight-id="${publicId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Brief flash so the user can visually connect the summary → card jump.
  el.classList.add('ring-2', 'ring-indigo-500/60');
  setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-500/60'), 1800);
}

// ---------- v3 Top-3 hero band ----------

/**
 * Top-3 lead band rendered above the WeeklySummaryPanel. The picks are
 * deterministic (filter + rank + cap, no LLM judgment); the headline +
 * narrative + infographic_brief are LLM-generated but constrained to
 * the selected public_ids. Imageless picks degrade gracefully — the
 * card renders the headline and narrative without the image slot.
 */
function TopPicksHero({
  picks,
  onPickClick,
}: {
  picks: TopPickEntry[];
  onPickClick: (insightPublicId: string) => void;
}): JSX.Element | null {
  if (!picks || picks.length === 0) return null;
  return (
    <section
      aria-label="This week's top picks"
      className="mb-6 rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-950/40 via-neutral-950/40 to-neutral-950/0 p-4 sm:p-5"
    >
      <header className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300/80">
            Top {picks.length} this week
          </p>
          <h2 className="mt-0.5 text-base font-semibold text-neutral-100 sm:text-lg">
            Read these first
          </h2>
        </div>
        <p className="hidden text-[11px] text-neutral-500 sm:block">
          Picked deterministically by relevance, fidelity + recency. Click "Open notebook" to clone.
        </p>
      </header>
      <ol className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3">
        {picks.map((pick, idx) => (
          <li
            key={pick.insight_public_id}
            className="group flex flex-col rounded-xl border border-neutral-800 bg-neutral-950/60 transition hover:border-indigo-500/40"
          >
            <div className="aspect-[16/9] w-full overflow-hidden rounded-t-xl bg-neutral-900">
              {pick.infographic_url ? (
                <img
                  src={pick.infographic_url}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[11px] font-mono uppercase tracking-wider text-neutral-600">
                  pick {idx + 1}
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-3">
              <h3 className="text-sm font-semibold leading-snug text-neutral-100">
                {pick.headline}
              </h3>
              <div className="flex-1">
                <MarkdownProse className="text-xs">
                  {pick.narrative_md}
                </MarkdownProse>
              </div>
              <div className="mt-auto flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => onPickClick(pick.insight_public_id)}
                  className="flex-1 rounded-md border border-indigo-500/40 bg-indigo-500/10 px-2.5 py-1 text-xs font-medium text-indigo-200 transition hover:bg-indigo-500/20"
                >
                  Read on the feed
                </button>
                <a
                  href={`/notebooks?from-research=true&insight=${encodeURIComponent(pick.insight_public_id)}`}
                  className="flex-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-center text-xs font-medium text-emerald-200 transition hover:bg-emerald-500/20"
                >
                  Open notebook
                </a>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function WeeklySummaryPanel({
  summary,
  insights,
  onThemeClick,
  activeThemeLabel,
}: {
  summary: WeeklySummaryResponse;
  insights: ResearchInsight[];
  onThemeClick: (theme: ThemeEntry) => void;
  activeThemeLabel: string | null;
}) {
  const insightById = useMemo(() => {
    const m = new Map<string, ResearchInsight>();
    for (const i of insights) m.set(i.public_id, i);
    return m;
  }, [insights]);

  // Collapsed by default on small screens would be ideal; for now default
  // expanded so returning readers see the letter. Key on week_iso so a week
  // switch resets the collapse state.
  const [expanded, setExpanded] = useState(true);
  useEffect(() => { setExpanded(true); }, [summary.week_iso]);

  const { content } = summary;
  const paperLabel = (pid: string): string => {
    const ins = insightById.get(pid);
    return ins ? ins.paper_title : `paper ${pid.slice(0, 8)}`;
  };

  return (
    <article className="mb-8 rounded-xl border border-neutral-800 bg-gradient-to-b from-neutral-950 to-neutral-950/60 shadow-[0_1px_0_rgba(255,255,255,0.02)_inset]">
      {/* Letter header — click anywhere on this row to collapse/expand */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="group flex w-full items-center justify-between gap-4 px-6 pt-5 pb-3 text-left"
        aria-expanded={expanded}
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-indigo-300/80">
            PropAlgos weekly letter
          </div>
          <div className="mt-1 text-base text-neutral-100">
            {formatWeekRange(summary.week_iso)}
          </div>
        </div>
        <span className="text-xs text-neutral-500 group-hover:text-neutral-300 transition">
          {expanded ? '− collapse' : '+ expand'}
        </span>
      </button>

      {!expanded && (
        <div className="px-6 pb-5 text-[13px] leading-relaxed text-neutral-400 line-clamp-2">
          <MarkdownProse>{content.intro}</MarkdownProse>
        </div>
      )}

      {expanded && (
        <div className="border-t border-neutral-800/70 px-6 pt-5 pb-6">
          {/* The generated intro IS the lead — no hardcoded salutation. */}
          <div className="mb-4 text-[15px] leading-[1.75] text-neutral-200">
            <MarkdownProse>{content.intro}</MarkdownProse>
          </div>

          {content.themes.length > 0 && (
            <div className="mb-5 flex flex-wrap gap-1.5">
              {content.themes.map((t, i) => {
                if (typeof t === 'string') {
                  // Legacy bare-string themes — no click wiring.
                  return (
                    <span
                      key={`${t}-${i}`}
                      className="rounded-full border border-neutral-700/40 bg-neutral-800/40 px-2.5 py-0.5 text-[11px] text-neutral-400"
                      title="This theme has no linked papers (legacy summary)"
                    >
                      {t}
                    </span>
                  );
                }
                const isActive = activeThemeLabel === t.label;
                const matchCount = t.insight_public_ids.filter((id) =>
                  insights.some((ins) => ins.public_id === id)
                ).length;
                return (
                  <button
                    key={`${t.label}-${i}`}
                    type="button"
                    onClick={() => onThemeClick(t)}
                    className={
                      'rounded-full border px-2.5 py-0.5 text-[11px] transition ' +
                      (isActive
                        ? 'border-indigo-400 bg-indigo-500/30 text-white'
                        : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200 hover:border-indigo-400/70 hover:bg-indigo-500/20 hover:text-white')
                    }
                    title={`Click to filter the feed to ${matchCount} paper${matchCount === 1 ? '' : 's'} under this theme`}
                  >
                    {t.label}
                    <span className="ml-1.5 text-indigo-300/70">({matchCount})</span>
                  </button>
                );
              })}
            </div>
          )}

          {content.excited_to_try.length > 0 && (
            <div className="mt-5 border-t border-neutral-800/40 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-emerald-300/80">
                What we're excited to try out
              </h3>
              <div className="space-y-4">
                {content.excited_to_try.map((item) => (
                  <div key={item.insight_public_id}>
                    <MarkdownProse className="text-[13px]">{item.blurb}</MarkdownProse>
                    <button
                      type="button"
                      onClick={() => scrollToInsightCard(item.insight_public_id)}
                      className="mt-1 text-[11px] italic text-neutral-500 hover:text-indigo-300 hover:underline"
                    >
                      jump to card ↓ {paperLabel(item.insight_public_id).slice(0, 70)}
                      {paperLabel(item.insight_public_id).length > 70 ? '…' : ''}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {content.still_early.length > 0 && (
            <div className="mt-5 border-t border-neutral-800/40 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-amber-300/80">
                What's still a bit early
              </h3>
              <div className="space-y-4">
                {content.still_early.map((item) => (
                  <div key={item.insight_public_id}>
                    <MarkdownProse className="text-[13px]">{item.blurb}</MarkdownProse>
                    <button
                      type="button"
                      onClick={() => scrollToInsightCard(item.insight_public_id)}
                      className="mt-1 text-[11px] italic text-neutral-500 hover:text-indigo-300 hover:underline"
                    >
                      jump to card ↓ {paperLabel(item.insight_public_id).slice(0, 70)}
                      {paperLabel(item.insight_public_id).length > 70 ? '…' : ''}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 border-t border-neutral-800/40 pt-4 text-sm leading-relaxed text-neutral-300">
            <MarkdownProse>{content.closing_nudge}</MarkdownProse>
          </div>

          {/* Letter signature */}
          <div className="mt-6 border-t border-neutral-800/40 pt-4 text-sm text-neutral-400">
            <div>Happy trading,</div>
            <div className="italic text-neutral-300">— the PropAlgos team</div>
          </div>
        </div>
      )}
    </article>
  );
}

// ---------- Filter bar ----------

function FilterBar({
  meta,
  sort,
  onSortChange,
  classification,
  onClassificationChange,
  timeframe,
  onTimeframeChange,
  category,
  onCategoryChange,
  hasCode,
  onHasCodeChange,
  hasNotebook,
  onHasNotebookChange,
  weekMode,
  week,
  onWeekModeChange,
  onWeekChange,
  query,
  onQueryChange,
  totalCount,
  loadedCount,
}: {
  meta: FeedMeta | null;
  sort: string;
  onSortChange: (v: string) => void;
  classification: ClassificationFilter;
  onClassificationChange: (v: ClassificationFilter) => void;
  timeframe: TimeframeFilter;
  onTimeframeChange: (v: TimeframeFilter) => void;
  category: string | null;
  onCategoryChange: (v: string | null) => void;
  hasCode: boolean;
  onHasCodeChange: (v: boolean) => void;
  hasNotebook: boolean;
  onHasNotebookChange: (v: boolean) => void;
  weekMode: 'single' | 'all';
  week: string;
  onWeekModeChange: (v: 'single' | 'all') => void;
  onWeekChange: (v: string) => void;
  query: string;
  onQueryChange: (v: string) => void;
  totalCount: number | null;
  loadedCount: number;
}) {
  const selectClass =
    'rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-xs text-white focus:border-neutral-600 focus:outline-none';

  // Week navigator: compute prev/next so the arrows can jump one week in
  // the meta-provided ordering (which is newest-first). "Previous" here
  // means the chronologically earlier week (index +1 in the list).
  const weeks = meta?.weeks ?? [];
  const weekIdx = weeks.findIndex((w) => w.iso === week);
  const hasPrev = weekMode === 'single' && weekIdx >= 0 && weekIdx < weeks.length - 1;
  const hasNext = weekMode === 'single' && weekIdx > 0;
  const goPrev = () => { if (hasPrev) onWeekChange(weeks[weekIdx + 1].iso); };
  const goNext = () => { if (hasNext) onWeekChange(weeks[weekIdx - 1].iso); };
  const goAll = () => { onWeekModeChange('all'); onWeekChange(''); };
  const goSingle = () => {
    onWeekModeChange('single');
    if (!week && weeks.length > 0) {
      const firstWithSummary = weeks.find((w) => w.has_summary);
      onWeekChange(firstWithSummary ? firstWithSummary.iso : weeks[0].iso);
    }
  };

  return (
    <section className="mb-5 rounded-lg border border-neutral-900 bg-neutral-950 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <FiFilter className="h-3.5 w-3.5" />
          <span className="font-medium">Filters</span>
        </div>

        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search papers…"
          aria-label="Search papers"
          className="w-48 rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-xs text-white placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none"
        />

        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          aria-label="Sort"
          className={selectClass}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={classification}
          onChange={(e) => onClassificationChange(e.target.value as ClassificationFilter)}
          aria-label="Classification"
          className={selectClass}
        >
          {CLASSIFICATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={timeframe}
          onChange={(e) => onTimeframeChange(e.target.value as TimeframeFilter)}
          aria-label="Published"
          className={selectClass}
        >
          {TIMEFRAME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Week navigator: ◀ [dropdown] ▶  |  All weeks toggle */}
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={goPrev}
            disabled={!hasPrev}
            aria-label="Previous week"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-800 bg-neutral-900 text-neutral-400 transition hover:border-neutral-600 hover:text-neutral-200 disabled:opacity-30 disabled:hover:border-neutral-800 disabled:hover:text-neutral-400"
          >
            ◀
          </button>
          <select
            value={week}
            onChange={(e) => {
              onWeekModeChange('single');
              onWeekChange(e.target.value);
            }}
            disabled={weekMode === 'all' || weeks.length === 0}
            aria-label="Week"
            className={`${selectClass} disabled:opacity-50`}
          >
            {weeks.length === 0 && <option value="">—</option>}
            {weeks.map((w) => (
              <option key={w.iso} value={w.iso}>
                {formatWeekRange(w.iso)}{w.has_summary ? ' · 📰' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={goNext}
            disabled={!hasNext}
            aria-label="Next week"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-800 bg-neutral-900 text-neutral-400 transition hover:border-neutral-600 hover:text-neutral-200 disabled:opacity-30 disabled:hover:border-neutral-800 disabled:hover:text-neutral-400"
          >
            ▶
          </button>
          <button
            type="button"
            onClick={weekMode === 'all' ? goSingle : goAll}
            className={`ml-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition ${
              weekMode === 'all'
                ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
                : 'border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-600'
            }`}
            title={weekMode === 'all' ? 'Pick a single week (shows the newsletter)' : 'Show cards across every week (hides the newsletter)'}
          >
            All weeks
          </button>
        </div>

        <button
          type="button"
          onClick={() => onHasCodeChange(!hasCode)}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition ${
            hasCode
              ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
              : 'border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-600'
          }`}
        >
          <FiGithub className="h-3 w-3" />
          Has code ↗
        </button>
        <button
          type="button"
          onClick={() => onHasNotebookChange(!hasNotebook)}
          title="Show only papers you've already generated a starter notebook for — your work-in-progress queue."
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition ${
            hasNotebook
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
              : 'border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-600'
          }`}
        >
          <FiBookOpen className="h-3 w-3" />
          Started by me
        </button>

        {totalCount != null && (
          <span className="ml-auto text-xs text-neutral-500">
            {loadedCount} of {totalCount}
          </span>
        )}
      </div>

      {meta?.categories && meta.categories.length > 0 && (
        <div className="mt-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <CategoryChip
            label="All"
            active={category == null}
            onClick={() => onCategoryChange(null)}
          />
          {meta.categories.map((c) => (
            <CategoryChip
              key={c.slug}
              label={c.label}
              active={category === c.slug}
              onClick={() => onCategoryChange(category === c.slug ? null : c.slug)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs transition ${
        active
          ? 'border-white/40 bg-white/10 text-white'
          : 'border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-600 hover:text-neutral-200'
      }`}
    >
      {label}
    </button>
  );
}

// ---------- Card ----------

function InsightCard({
  insight,
  onDismiss,
  onOpenTemplate,
  onGenerateStarter,
  onOpenNotebook,
  onGenerateNew,
  onRequestDataset,
  dismissing,
  generating,
  alreadyRequested,
  requestingDataset,
}: {
  insight: ResearchInsight;
  onDismiss: (id: string) => void;
  onOpenTemplate: (slug: string) => void;
  onGenerateStarter: (insight: ResearchInsight) => void;
  onOpenNotebook: (insight: ResearchInsight) => void;
  onGenerateNew: (insight: ResearchInsight) => void;
  onRequestDataset: (insight: ResearchInsight) => void;
  dismissing: boolean;
  generating: boolean;
  alreadyRequested: boolean;
  requestingDataset: boolean;
}) {
  const pubList = (insight.source_publications || [])
    .map((p) => p.display_name || p.slug)
    .filter(Boolean);
  const pubSingle = pubList[0];
  const badgeClass = RELEVANCE_BADGE[insight.propalgos_relevance];
  const hasStarterNotebook = (insight.starter_notebook?.count ?? 0) > 0;
  const isFindings = insight.classification === 'findings_only';
  const isSketch = insight.classification === 'notebook_sketch';
  const isHighFidelity = insight.classification === 'notebook_high_fidelity';
  const canGenerate = Boolean(insight.actionability?.notebook_recipe);

  const accentClass = hasStarterNotebook
    ? 'border-l-2 border-l-emerald-500/50'
    : isFindings
      ? 'border-l-2 border-l-indigo-500/40'
      : isHighFidelity
        ? 'border-l-2 border-l-emerald-500/30'
        : isSketch
          ? 'border-l-2 border-l-amber-500/40'
          : '';

  const missingDatasets = insight.actionability?.missing_datasets ?? [];
  const canRequestDataset = missingDatasets.length > 0;

  return (
    <article
      data-insight-id={insight.public_id}
      className={`relative rounded-lg border border-neutral-800 bg-neutral-950 px-5 py-4 transition-shadow ${accentClass} ${
        insight.dismissed ? 'opacity-50' : ''
      }`}
    >
      {!insight.dismissed && (
        <button
          type="button"
          onClick={() => onDismiss(insight.public_id)}
          disabled={dismissing}
          title="Hide this insight"
          aria-label="Hide this insight"
          className="absolute right-3 top-3 rounded p-1 text-neutral-600 transition hover:bg-neutral-800 hover:text-neutral-300 disabled:opacity-50"
        >
          <FiX className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="flex items-start justify-between gap-3 pr-6">
        <div className="flex-1 min-w-0">
          <div className="mb-1.5 flex items-center gap-2 text-xs text-neutral-500">
            <span title={insight.week_iso}>{formatWeekRange(insight.week_iso)}</span>
            {insight.source_type && (
              <>
                <span>·</span>
                <span className="uppercase tracking-wide">{insight.source_type}</span>
              </>
            )}
            <span>·</span>
            {pubList.length >= 2 ? (
              <span
                title={pubList.join(', ')}
                className="inline-flex shrink-0 items-center gap-1 text-neutral-400"
              >
                <FiStar className="h-3 w-3" />
                {pubList.length} sources
              </span>
            ) : (
              <span className="truncate">{pubSingle || 'unknown source'}</span>
            )}
            {insight.paper_published_at && (
              <>
                <span>·</span>
                <span title="Paper publication date">
                  {new Date(insight.paper_published_at).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
          <h2 className="text-base font-semibold leading-tight text-white">
            <a
              href={insight.paper_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-neutral-200"
            >
              {insight.paper_title}
              <FiExternalLink className="h-3.5 w-3.5 opacity-60" />
            </a>
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {insight.corroborated && (
            <span
              title="Corroborated: two or more independent quant newsletters flagged this paper this week. Shared editorial attention is a weak but real signal that this thread has mindshare."
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300"
            >
              <FiStar className="h-3 w-3" />
              Corroborated
            </span>
          )}
          <span
            title={relevanceTooltip(insight.propalgos_relevance)}
            className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${badgeClass}`}
          >
            {insight.propalgos_relevance}
          </span>
          <ClassificationBadge classification={insight.classification} fidelity={insight.fidelity_score} />
          {hasStarterNotebook && (
            <span
              title={`You've already generated ${insight.starter_notebook!.count} starter notebook${insight.starter_notebook!.count === 1 ? '' : 's'} for this paper. Click "Open notebook" below to jump back in.`}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300"
            >
              <FiBookOpen className="h-3 w-3" />
              Generated
            </span>
          )}
        </div>
      </div>

      {insight.infographic_url && (
        <div className="mt-3 overflow-hidden rounded-md border border-neutral-800 bg-neutral-950">
          <img
            src={insight.infographic_url}
            alt={`Infographic for ${insight.paper_title}`}
            loading="lazy"
            className="block w-full h-auto"
          />
        </div>
      )}

      {isFindings ? (
        <FindingsBody actionability={insight.actionability!} summary={insight.summary_md} />
      ) : (
        <SummarySection
          summary={insight.summary_md}
          classification={insight.classification}
          narrativeMd={insight.actionability?.notebook_recipe?.narrative_md ?? null}
        />
      )}

      {!isFindings && insight.recommended_gpu_tier && tierDisplay(insight.recommended_gpu_tier) && (
        <p className="mt-2 text-[11px] text-neutral-500">
          Runs on {tierDisplay(insight.recommended_gpu_tier)}
        </p>
      )}

      {insight.feature_tags && insight.feature_tags.length > 0 && (
        <div className="mt-3 flex min-h-[1.25rem] flex-wrap gap-1.5">
          {insight.feature_tags.map((tag) => (
            <span
              key={tag}
              className="rounded-sm bg-neutral-800/50 px-1.5 py-0.5 text-[10px] text-neutral-400"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {insight.code_references && insight.code_references.length > 0 && (
        <CodeReferencesRow refs={insight.code_references} />
      )}

      {!isFindings && insight.actionability && (
        <NotebookActionabilitySection actionability={insight.actionability} />
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!isFindings && hasStarterNotebook ? (
          <>
            <button
              type="button"
              onClick={() => onOpenNotebook(insight)}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black transition hover:bg-neutral-200"
            >
              <FiFileText className="h-3.5 w-3.5" />
              Open notebook
            </button>
            {canGenerate && (
              <button
                type="button"
                onClick={() => onGenerateNew(insight)}
                disabled={generating}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-200 transition hover:border-neutral-500 disabled:opacity-60"
              >
                <FiPlus className="h-3.5 w-3.5" />
                {generating ? 'Generating...' : 'Generate new'}
              </button>
            )}
          </>
        ) : !isFindings && canGenerate ? (
          <button
            type="button"
            onClick={() => onGenerateStarter(insight)}
            disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black transition hover:bg-neutral-200 disabled:opacity-60"
          >
            <FiPlay className="h-3.5 w-3.5" />
            {generating ? 'Generating...' : 'Generate starter notebook'}
          </button>
        ) : null}
        {insight.related_template_slug && (
          <button
            type="button"
            onClick={() => onOpenTemplate(insight.related_template_slug!)}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-200 transition hover:border-neutral-500"
          >
            <FiCheck className="h-3.5 w-3.5" />
            Use matching template
          </button>
        )}
        {canRequestDataset && (
          alreadyRequested ? (
            <span
              title={`Dataset request logged: ${missingDatasets.slice(0, 3).join(', ')}${missingDatasets.length > 3 ? '…' : ''}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-300"
            >
              <FiCheck className="h-3.5 w-3.5" />
              Dataset requested
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onRequestDataset(insight)}
              disabled={requestingDataset}
              title={`Tell us you want: ${missingDatasets.slice(0, 3).join(', ')}${missingDatasets.length > 3 ? '…' : ''}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/5 px-3 py-1.5 text-xs font-medium text-sky-300 transition hover:bg-sky-500/15 disabled:opacity-60"
            >
              <FiDatabase className="h-3.5 w-3.5" />
              {requestingDataset ? 'Requesting…' : 'Request this dataset'}
            </button>
          )
        )}
      </div>
    </article>
  );
}

function ClassificationBadge({
  classification,
  fidelity,
}: {
  classification: Classification | null;
  fidelity: number | null;
}) {
  if (!classification) return null;
  if (classification === 'findings_only') {
    return (
      <span
        title="Findings only — worth reading for the insight, but the paper's methodology isn't reducible to a runnable notebook on PropAlgos today (data we don't have, infrastructure we can't spin up, or theory that doesn't lend itself to a backtest)."
        className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-300"
      >
        <FiEye className="h-3 w-3" />
        Read
      </span>
    );
  }
  const pct = fidelity != null ? Math.round(fidelity * 100) : null;
  const isHigh = classification === 'notebook_high_fidelity';
  return (
    <span
      title={
        isHigh
          ? 'Reproducible: this paper can be generated as a starter notebook that closely mirrors its methodology using PropAlgos datasets and SDK. One click from the "Generate starter notebook" button → a working backtest.'
          : `Sketch: this paper's core idea can be generated as a starter notebook, but the fidelity score (${pct ?? '??'}%) captures how much of the original methodology we simplify. Common simplifications: daily OHLCV instead of intraday, equity proxies for unavailable assets, or a lighter model than the paper's. Useful as a starting point, not a perfect replication.`
      }
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        isHigh
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
          : 'border-amber-500/40 bg-amber-500/10 text-amber-300'
      }`}
    >
      {isHigh ? 'Reproducible' : 'Sketch'}
      {pct != null && <span className="font-mono opacity-80">~{pct}%</span>}
    </span>
  );
}

// Prose-styled markdown + LaTeX renderer. Used by both SummarySection
// (notebook_* narrative) and FindingsBody (findings summary) so math like
// `$h = (\rho \sigma_S \sigma_F) / (\sigma_F^2 + \Theta_F)$` renders
// correctly. Keeps typography tight to match card density.
// M6/M7: the feed renders Bedrock/Opus-authored markdown (intro, blurb,
// closing_nudge, narrative_md) — attacker-influenced content on a PUBLIC page.
// react-markdown blocks raw HTML, but a bare `[x](javascript:...)` link or a
// `![](http://attacker/beacon.gif)` image would otherwise render. Allow only
// https:/mailto: URLs, and only render images served from our own shared S3
// bucket (presigned infographic URLs) — everything else is dropped.
function safeMarkdownUrl(url: string): string {
  try {
    if (url.startsWith('mailto:')) return url;
    const u = new URL(url, window.location.origin);
    return u.protocol === 'https:' ? url : '';
  } catch {
    return ''; // unparseable / relative-without-base → drop
  }
}

const _SHARED_BUCKET_IMG = /^https:\/\/propalgos-[\w-]+\.s3[.-][\w.-]*amazonaws\.com\//i;

// singleDollarTextMath: false — letter prose is full of currency amounts
// ("$2.6B ... $1 billion"), and single-$ math delimiters turned everything
// between two amounts into italic KaTeX gibberish, swallowing markdown
// links. Real math still renders via $$...$$ blocks.
const _REMARK_PLUGINS: any = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]];

function MarkdownProse({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  return (
    <div className={`max-w-none text-neutral-300 ${className ?? ''}`}>
      <ReactMarkdown
        remarkPlugins={_REMARK_PLUGINS}
        rehypePlugins={[rehypeKatex]}
        urlTransform={safeMarkdownUrl}
        components={{
          img: ({ src, alt }) =>
            typeof src === 'string' && _SHARED_BUCKET_IMG.test(src)
              ? <img src={src} alt={alt ?? ''} className="my-2 max-w-full rounded-md border border-neutral-800" />
              : null,
          h1: ({ children }) => <h3 className="mt-0 mb-1 text-sm font-semibold text-neutral-200">{children}</h3>,
          h2: ({ children }) => <h4 className="mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">{children}</h4>,
          h3: ({ children }) => <h5 className="mt-2 mb-1 text-xs font-semibold text-neutral-300">{children}</h5>,
          p: ({ children }) => <p className="my-1.5 text-sm leading-relaxed text-neutral-300">{children}</p>,
          ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5 text-sm text-neutral-400">{children}</ul>,
          ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5 text-sm text-neutral-400">{children}</ol>,
          li: ({ children }) => <li className="text-sm">{children}</li>,
          code: ({ className, children, ...props }) => {
            const isBlock = className?.includes('language-');
            if (isBlock) {
              return (
                <pre className="my-2 overflow-x-auto rounded-md border border-neutral-800 bg-neutral-900/60 p-2 text-xs">
                  <code className="font-mono text-neutral-200" {...props}>{children}</code>
                </pre>
              );
            }
            return <code className="rounded-sm bg-neutral-800/60 px-1 py-0.5 font-mono text-[11px] text-neutral-200" {...props}>{children}</code>;
          },
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline">
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="font-semibold text-neutral-200">{children}</strong>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

function SummarySection({
  summary,
  classification,
  narrativeMd,
}: {
  summary: string | null;
  classification?: Classification | null;
  narrativeMd?: string | null;
}) {
  const isNotebook = classification?.startsWith('notebook_') ?? false;
  // For notebook_* cards, prefer the LLM-authored recipe narrative over the
  // raw `summary_md` (often a substack tagline stub). Findings cards go
  // through FindingsBody, not here.
  const preferred = isNotebook && narrativeMd?.trim()
    ? narrativeMd.trim()
    : (summary ?? '').trim();
  if (!preferred) {
    return (
      <div className={`mt-3 ${SUMMARY_MIN_HEIGHT_CLASS}`}>
        <p className="text-sm italic leading-relaxed text-neutral-500">
          No abstract available from source — click the paper title to read it in full.
        </p>
      </div>
    );
  }
  const isTagline = preferred.length < TAGLINE_THRESHOLD;
  // Suppress the tagline chip on notebook_* cards — the recipe is the real
  // content there, and the chip reads as a contradiction next to the SDK
  // call list and recipe narrative.
  const showTaglineChip = isTagline && !isNotebook;
  return (
    <div className={`mt-3 ${SUMMARY_MIN_HEIGHT_CLASS}`}>
      {showTaglineChip && (
        <span className="mb-1.5 inline-flex items-center rounded-sm bg-neutral-800/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
          Tagline only
        </span>
      )}
      {isNotebook ? (
        <MarkdownProse>{preferred}</MarkdownProse>
      ) : (
        <p
          className={
            isTagline
              ? 'text-sm leading-relaxed text-neutral-300'
              : 'text-sm leading-relaxed text-neutral-400'
          }
        >
          {preferred}
        </p>
      )}
    </div>
  );
}

function FindingsBody({
  actionability,
  summary,
}: {
  actionability: Actionability;
  summary: string | null;
}) {
  const findings = (actionability.findings_summary ?? '').trim();
  const displaySummary = findings || (summary ?? '').trim();
  const reason = actionability.findings_only_reason;
  return (
    <div className={`mt-3 ${SUMMARY_MIN_HEIGHT_CLASS}`}>
      {displaySummary ? (
        <p className="text-sm leading-relaxed text-neutral-200">{displaySummary}</p>
      ) : (
        <p className="text-sm italic leading-relaxed text-neutral-500">
          Worth reading — open the paper for details.
        </p>
      )}
      {actionability.key_findings && actionability.key_findings.length > 0 && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-neutral-400">
          {actionability.key_findings.map((k, i) => (
            <li key={i}>{k}</li>
          ))}
        </ul>
      )}
      {reason && (
        <p className="mt-3 text-[11px] italic text-indigo-300/70">
          {REASON_LABEL[reason] || reason}
        </p>
      )}
    </div>
  );
}

function CodeReferencesRow({ refs }: { refs: CodeReference[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {refs.slice(0, 4).map((r) => (
        <a
          key={r.url}
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] transition hover:border-neutral-500 ${
            r.is_official
              ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300'
              : 'border-neutral-800 bg-neutral-900 text-neutral-400'
          }`}
          title={r.is_official ? 'Author-official reference code' : 'Community implementation'}
        >
          <FiGithub className="h-3 w-3" />
          <span className="max-w-[200px] truncate font-mono">
            {r.url.replace(/^https?:\/\/(www\.)?/, '')}
          </span>
          {r.framework && <span className="opacity-80">· {r.framework}</span>}
          {r.stars != null && <span className="opacity-80">· {formatStars(r.stars)}★</span>}
        </a>
      ))}
    </div>
  );
}

function formatStars(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function NotebookActionabilitySection({ actionability }: { actionability: Actionability }) {
  const {
    datasets_referenced: datasets,
    sdk_functions_used: sdkFns,
    next_steps: nextSteps,
    known_gaps: knownGaps,
  } = actionability;

  // GPU tier is now rendered at the card level as a subtle "Runs on …"
  // line; keep it out of the chip row to avoid duplication.
  const hasChips = datasets.length > 0 || sdkFns.length > 0;
  const hasSteps = nextSteps.length > 0;
  const hasGaps = knownGaps && knownGaps.length > 0;
  const hasDetails = hasSteps || hasGaps;

  // SDK chips: show inline when <=3; collapse behind a toggle otherwise so
  // the action bar doesn't hog a full row on papers with many SDK calls.
  const [sdkExpanded, setSdkExpanded] = useState<boolean>(sdkFns.length <= 3);
  // Recipe details: expand on demand. Default collapsed so sketch cards
  // match the visual height of findings cards unless the user opts in.
  const [detailsExpanded, setDetailsExpanded] = useState<boolean>(false);

  if (!hasChips && !hasDetails) return null;

  return (
    <div className="mt-4 rounded-md border border-neutral-800 bg-neutral-900/40 p-3">
      {hasChips && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {datasets.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1 rounded-sm bg-sky-500/10 px-2 py-0.5 text-sky-300"
            >
              <FiDatabase className="h-3 w-3" /> {d}
            </span>
          ))}
          {sdkExpanded ? (
            <>
              {sdkFns.map((fn) => (
                <span
                  key={fn}
                  className="inline-flex items-center gap-1 rounded-sm bg-indigo-500/10 px-2 py-0.5 font-mono text-indigo-300"
                >
                  <FiCode className="h-3 w-3" /> {fn}
                </span>
              ))}
              {sdkFns.length > 3 && (
                <button
                  type="button"
                  onClick={() => setSdkExpanded(false)}
                  className="text-[11px] text-neutral-500 underline-offset-2 hover:text-neutral-300 hover:underline"
                >
                  hide
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => setSdkExpanded(true)}
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-800 bg-neutral-900 px-2 py-0.5 text-[11px] text-neutral-400 hover:border-neutral-600 hover:text-neutral-200"
            >
              <FiCode className="h-3 w-3" />
              {sdkFns.length} SDK calls ▾
            </button>
          )}
        </div>
      )}
      {hasDetails && (
        <div className="mt-3">
          {!detailsExpanded ? (
            <button
              type="button"
              onClick={() => setDetailsExpanded(true)}
              className="text-[11px] text-neutral-500 underline-offset-2 hover:text-neutral-300 hover:underline"
            >
              Show recipe details ▾
            </button>
          ) : (
            <>
              {hasSteps && (
                <ol className="list-decimal space-y-1 pl-5 text-xs text-neutral-400">
                  {nextSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              )}
              {hasGaps && (
                <div className="mt-3">
                  <div className="mb-1 text-xs font-medium text-amber-400/80">
                    Known fidelity gaps
                  </div>
                  <ul className="list-disc space-y-0.5 pl-5 text-xs text-neutral-500">
                    {knownGaps.map((g, i) => (
                      <li key={i}>{g}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                onClick={() => setDetailsExpanded(false)}
                className="mt-3 text-[11px] text-neutral-500 underline-offset-2 hover:text-neutral-300 hover:underline"
              >
                Hide details
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Overwrite dialog ----------

function OverwriteDialog({
  state,
  onCancel,
  onConfirm,
}: {
  state: OverwriteDialogState;
  onCancel: () => void;
  onConfirm: (mode: 'overwrite' | 'timestamped') => void;
}) {
  const { existing } = state;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-lg border border-neutral-800 bg-neutral-950 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-white">Notebook already exists</h2>
        <p className="mt-2 text-sm text-neutral-400">
          You already generated a starter notebook for this insight. Choose how to
          handle your existing file.
        </p>
        <div className="mt-4 max-h-40 overflow-y-auto rounded-md border border-neutral-800 bg-neutral-900/40 p-2 text-xs">
          {existing.versions.map((v) => (
            <div key={v.key} className="flex items-center justify-between py-1">
              <span className="truncate pr-2 font-mono text-neutral-400" title={v.key}>
                {v.key.split('/').pop()}
              </span>
              <span className="shrink-0 text-neutral-600">
                {v.last_modified ? new Date(v.last_modified).toLocaleString() : ''}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:border-neutral-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm('timestamped')}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-200 hover:border-neutral-500"
          >
            Keep both (timestamped)
          </button>
          <button
            type="button"
            onClick={() => onConfirm('overwrite')}
            className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-black hover:bg-neutral-200"
          >
            Overwrite in place
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Empty state ----------

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-neutral-800 bg-neutral-950 py-16 text-center">
      <FiRss className="mx-auto h-8 w-8 text-neutral-600" />
      <h2 className="mt-4 text-lg font-medium text-neutral-200">No insights match these filters</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Try widening the timeframe, clearing the category, or switching to "All cards."
      </p>
    </div>
  );
}
